import { useEffect, useReducer, useRef, useState } from 'react'
import SEO from './components/SEO'
import HandoffSteps from './components/HandoffSteps'
import SiteNav from './components/SiteNav'
import Footer from './components/Footer'
import AgentLauncher, { INSTALL_COMMANDS } from './components/AgentLauncher'
import { useCopyPrompt } from './hooks/useCopyPrompt'

const HOME_TITLE = 'OpenDeploy — Hey Claude, deploy this app with /opendeploy'
const HOME_DESCRIPTION =
  'Say /opendeploy to Claude Code, OpenCode, Codex, Cursor, Kiro, OpenClaw, Hermes, or any coding agent. OpenDeploy turns the instruction into a live URL.'

const HOME_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://opendeploy.dev/#webpage',
  url: 'https://opendeploy.dev/',
  name: HOME_TITLE,
  description: HOME_DESCRIPTION,
  isPartOf: { '@id': 'https://opendeploy.dev/#website' },
  about: { '@id': 'https://opendeploy.dev/#software' },
  primaryImageOfPage: 'https://opendeploy.dev/og-image.png',
  inLanguage: 'en',
}

interface AgentEvent {
  label: string
  detail: string
  thinkingMs: number
  workMs: number
}

const AGENT_EVENTS: AgentEvent[] = [
  { label: 'reading project context', detail: 'repo files · package scripts · env hints', thinkingMs: 550, workMs: 780 },
  { label: 'detecting runtime and services', detail: 'node 20 · web · worker · postgres', thinkingMs: 550, workMs: 900 },
  { label: 'creating OpenDeploy service', detail: 'project · service · environment', thinkingMs: 500, workMs: 760 },
  { label: 'deploying app', detail: 'build · push · provision · go live', thinkingMs: 600, workMs: 1100 },
]

const LIVE_URL = 'your-app.opendeploy.site'
const INSTALL_TERMINAL_COMMAND = INSTALL_COMMANDS[0].command
const DEMO_AGENT_PROMPT = 'Deploy this app with /opendeploy.'

const HERO_AGENTS = ['Claude', 'OpenCode', 'Codex', 'Cursor', 'Kiro', 'OpenClaw', 'Hermes'] as const

const AGENT_BADGES = [
  { id: 'claude', label: 'Claude Code', icon: '/agent-icons/claude.svg' },
  { id: 'opencode', label: 'OpenCode', initials: 'OC' },
  { id: 'codex', label: 'Codex', icon: '/agent-icons/chatgpt.svg' },
  { id: 'cursor', label: 'Cursor', icon: '/agent-icons/cursor.svg' },
  { id: 'openclaw', label: 'OpenClaw', icon: '/agent-icons/openclaw.jpg' },
  { id: 'hermes', label: 'Hermes', icon: '/agent-icons/hermes.png' },
  { id: 'more', label: 'More agents' },
] as const

type ChatState = {
  typedChars: number
  showUser: boolean
  agentVisible: boolean
  doneCount: number
  thinkingIndex: number | null
  showLive: boolean
}

type ChatAction =
  | { type: 'reset' }
  | { type: 'type'; n: number }
  | { type: 'sendUser' }
  | { type: 'showAgent' }
  | { type: 'startThinking'; index: number }
  | { type: 'completeStep'; index: number }
  | { type: 'showLive' }

const INITIAL_STATE: ChatState = {
  typedChars: 0,
  showUser: false,
  agentVisible: false,
  doneCount: 0,
  thinkingIndex: null,
  showLive: false,
}

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'reset':
      return INITIAL_STATE
    case 'type':
      return { ...state, typedChars: action.n }
    case 'sendUser':
      return { ...state, showUser: true, typedChars: 0 }
    case 'showAgent':
      return { ...state, agentVisible: true }
    case 'startThinking':
      return { ...state, thinkingIndex: action.index }
    case 'completeStep':
      return { ...state, thinkingIndex: null, doneCount: action.index + 1 }
    case 'showLive':
      return { ...state, thinkingIndex: null, showLive: true }
    default:
      return state
  }
}

function AgentBadgeStrip() {
  return (
    <div className="od-agent-strip" aria-label="Supported coding agents">
      {AGENT_BADGES.map((agent) => (
        <span
          key={agent.id}
          className={`od-agent-icon od-agent-icon-${agent.id}`}
          role="img"
          aria-label={agent.label}
          title={agent.label}
        >
          {'icon' in agent ? (
            <img src={agent.icon} alt="" decoding="async" />
          ) : 'initials' in agent ? (
            <span className="od-agent-initials" aria-hidden="true">{agent.initials}</span>
          ) : (
            <span className="od-agent-more" aria-hidden="true">•••</span>
          )}
        </span>
      ))}
    </div>
  )
}

function RotatingAgentName() {
  const [agentIndex, setAgentIndex] = useState(0)

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return undefined

    const intervalId = window.setInterval(() => {
      setAgentIndex((index) => (index + 1) % HERO_AGENTS.length)
    }, 1700)

    return () => window.clearInterval(intervalId)
  }, [])

  const agent = HERO_AGENTS[agentIndex]

  return (
    <span className="od-rotating-agent">
      <span key={agent} className="od-rotating-agent-name">{agent},</span>
    </span>
  )
}

function InstallTerminalDemo() {
  const sectionRef = useRef<HTMLElement | null>(null)
  const [isRunning, setIsRunning] = useState(false)

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return undefined

    if (typeof IntersectionObserver === 'undefined') {
      setIsRunning(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRunning(true)
          observer.disconnect()
        }
      },
      { threshold: 0.55 },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <section
      ref={sectionRef}
      className={`od-install-terminal-scene${isRunning ? ' is-running' : ''}`}
      aria-label="Install OpenDeploy in Terminal"
    >
      <div className="od-mac-terminal">
        <div className="od-mac-terminal-titlebar">
          <span className="od-mac-terminal-controls" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="od-mac-terminal-title">opendeploy install — zsh</span>
        </div>
        <div className="od-mac-terminal-body">
          <span className="od-mac-terminal-line">
            <span className="od-mac-terminal-prompt" aria-hidden="true">%</span>
            <span className="od-mac-terminal-command">
              <code>{INSTALL_TERMINAL_COMMAND}</code>
              <span className="od-mac-terminal-cursor" aria-hidden="true" />
              <span className="od-mac-terminal-send" aria-hidden="true">↵</span>
            </span>
          </span>
          <div className="od-mac-terminal-output">
            <span>Installing OpenDeploy CLI</span>
            <span>Installing agent skill</span>
            <span>Ready. Send /opendeploy to your agent</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function LiveAgentChat() {
  const [state, dispatch] = useReducer(chatReducer, INITIAL_STATE)
  const timeouts = useRef<number[]>([])
  const conversationRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const queue = (delay: number, fn: () => void) => {
      const id = window.setTimeout(fn, delay)
      timeouts.current.push(id)
    }

    const run = () => {
      timeouts.current.forEach((id) => window.clearTimeout(id))
      timeouts.current = []
      dispatch({ type: 'reset' })

      let cursor = 620

      queue(cursor, () => dispatch({ type: 'type', n: DEMO_AGENT_PROMPT.length }))
      cursor += 420

      queue(cursor, () => dispatch({ type: 'sendUser' }))
      cursor += 380

      queue(cursor, () => dispatch({ type: 'showAgent' }))
      cursor += 300

      AGENT_EVENTS.forEach((event, index) => {
        queue(cursor, () => dispatch({ type: 'startThinking', index }))
        cursor += event.thinkingMs
        queue(cursor, () => dispatch({ type: 'completeStep', index }))
        cursor += event.workMs
      })

      queue(cursor, () => dispatch({ type: 'startThinking', index: AGENT_EVENTS.length }))
      cursor += 650
      queue(cursor, () => dispatch({ type: 'showLive' }))
      cursor += 6500

      queue(cursor, run)
    }

    run()

    return () => {
      timeouts.current.forEach((id) => window.clearTimeout(id))
      timeouts.current = []
    }
  }, [])

  useEffect(() => {
    const node = conversationRef.current
    if (!node) return
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
  }, [state.doneCount, state.thinkingIndex, state.showLive, state.agentVisible, state.showUser])

  const typedText = DEMO_AGENT_PROMPT.slice(0, state.typedChars)
  const composerPlaceholder = !state.showUser && state.typedChars === 0
  const isStreamingFinal = state.thinkingIndex === AGENT_EVENTS.length

  return (
    <aside className="od-demo" aria-label="Animated agent chat demo">
      <div className="od-demo-head">
        <AgentBadgeStrip />
      </div>

      <div className="od-demo-body">
        <div className="od-demo-conversation" ref={conversationRef}>
          {!state.showUser && !state.agentVisible && (
            <div className="od-demo-hint" aria-hidden="true">
              you paste the instruction · the agent takes it from here
            </div>
          )}

          {state.showUser && (
            <div className="od-demo-bubble od-demo-bubble-user">
              <span className="od-demo-bubble-meta">you</span>
              <span>{DEMO_AGENT_PROMPT}</span>
            </div>
          )}

          {state.agentVisible && (
            <div className="od-demo-bubble od-demo-bubble-agent">
              <span className="od-demo-bubble-meta">
                <span className="od-demo-pulse" aria-hidden="true" />
                your agent
              </span>

              <ol className="od-demo-events">
                {AGENT_EVENTS.slice(0, state.doneCount).map((event) => (
                  <li key={event.label} className="od-demo-event is-done">
                    <span className="od-demo-check" aria-hidden="true" />
                    <span className="od-demo-event-text">
                      <span className="od-demo-event-label">{event.label}</span>
                      <span className="od-demo-event-detail">{event.detail}</span>
                    </span>
                  </li>
                ))}

                {state.thinkingIndex !== null && state.thinkingIndex < AGENT_EVENTS.length && (
                  <li className="od-demo-event is-thinking">
                    <span className="od-demo-spinner" aria-hidden="true" />
                    <span className="od-demo-event-text">
                      <span className="od-demo-event-label">
                        {AGENT_EVENTS[state.thinkingIndex].label}
                      </span>
                      <span className="od-demo-typing" aria-hidden="true">
                        <span /><span /><span />
                      </span>
                    </span>
                  </li>
                )}

                {isStreamingFinal && !state.showLive && (
                  <li className="od-demo-event is-thinking">
                    <span className="od-demo-spinner" aria-hidden="true" />
                    <span className="od-demo-event-text">
                      <span className="od-demo-event-label">finalizing live URL</span>
                      <span className="od-demo-typing" aria-hidden="true">
                        <span /><span /><span />
                      </span>
                    </span>
                  </li>
                )}

                {state.showLive && (
                  <li className="od-demo-event is-live">
                    <span className="od-demo-live-dot" aria-hidden="true" />
                    <span className="od-demo-event-text">
                      <span className="od-demo-event-label">your app is live</span>
                      <span className="od-demo-live-url">https://{LIVE_URL}</span>
                    </span>
                  </li>
                )}
              </ol>
            </div>
          )}
        </div>

        <div
          className={`od-demo-composer${composerPlaceholder ? ' is-placeholder' : ''}${state.typedChars === DEMO_AGENT_PROMPT.length && !state.showUser ? ' is-ready' : ''}`}
        >
          <span className="od-demo-input">
            {composerPlaceholder ? (
              <span className="od-demo-input-placeholder">message your agent…</span>
            ) : (
              <>
                <span>{typedText}</span>
                {!state.showUser && <span className="od-demo-caret" aria-hidden="true" />}
              </>
            )}
          </span>
          <button
            type="button"
            className="od-demo-send"
            disabled
            tabIndex={-1}
            aria-hidden="true"
          >
            send
          </button>
        </div>
      </div>
    </aside>
  )
}

export default function Landing() {
  const { copied, copy } = useCopyPrompt()

  useEffect(() => {
    document.body.classList.add('v2-home-page')
    return () => document.body.classList.remove('v2-home-page')
  }, [])

  useEffect(() => {
    if (window.location.hash !== '#how') return

    const timeoutId = window.setTimeout(() => {
      document.getElementById('how')?.scrollIntoView({ block: 'start' })
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [])

  return (
    <>
      <SEO
        title={HOME_TITLE}
        description={HOME_DESCRIPTION}
        path="/"
        jsonLd={HOME_JSONLD}
      />

      <div className="od-home">
        <SiteNav />

        <main className="od-main">
          <div className="od-hero-shell">
            <section className="od-hero" aria-labelledby="home-title">
              <h1 id="home-title" className="od-command-title">
                <span className="v2-sr-only">
                  Hey Claude, OpenCode, Codex, Cursor, Kiro, OpenClaw, or Hermes, deploy this app with /opendeploy.
                </span>
                <span className="od-command-prompt" aria-hidden="true">
                  hey <RotatingAgentName />
                </span>
                <span className="od-command-line" aria-hidden="true">
                  <span className="od-command-main">deploy this app</span>
                  <span className="od-command-with">
                    with <code>/opendeploy</code>
                  </span>
                </span>
              </h1>
            </section>

            <AgentLauncher
              copied={copied}
              onCopy={copy}
              className="od-hero-launcher"
              variant="hero"
            />
          </div>

          <InstallTerminalDemo />
          <LiveAgentChat />
          <HandoffSteps />
        </main>

        <Footer />
      </div>
    </>
  )
}
